/**
 * Extern Labs PUBLIC PREVIEW, Experiment 4's event vocabulary.
 *
 * Experiment 4 puts a shortened Labs sprint on a public page linked from the pSEO internship guides.
 * It runs in the SAME Mixpanel project as Experiment 3 (the intent overlay, enrolled 2026-08-24), which
 * is the reason this file exists at all.
 *
 * 🔴 NOT ONE STRING HERE MAY APPEAR IN ANY `*_discovery_*` EVENT. The three vertical event files are
 * the Experiment 3 vocabulary; every one of their names is `{vertical}_discovery_*`, and every board
 * card and funnel is keyed on those literals. This file is uniformly `labs_preview_*`, so the two
 * populations cannot be pooled by accident even by a careless funnel. An Experiment 4 event turning up
 * inside an Experiment 3 denominator is a bug, not a cohort.
 *
 * The two surfaces are ALSO physically disjoint, which is the belt to this file's braces: the preview
 * lives on a public route outside the paywall, its readers are signed out, and it never reads the Labs
 * arm flag. So a preview reader cannot be enrolled in Experiment 3, and an Experiment 3 learner cannot
 * emit these events.
 *
 * WHAT THIS EXPERIMENT MEASURES. Not trial-to-paid, and not "leads". A reader finishes the shortened
 * lab, gives an email, and is handed to sign-up: SIGNUPS are the outcome. So the chain that matters is
 * VIEWED → COMPLETED → EMAIL_SUBMITTED → SIGNUP_HANDOFF, each over the one before it, with
 * `guide_source` on every event so it reads per guide page rather than only in aggregate.
 */
export const LABS_PREVIEW_EVENTS = {
  /**
   * The page was opened. The DENOMINATOR for everything else, and deliberately separate from the
   * platform's automatic pageview so it carries `vertical` and `guide_source` from the first beat.
   */
  VIEWED: 'labs_preview_viewed',
  /** The reader started the lab (left the welcome screen). VIEWED minus this is the cold bounce. */
  STARTED: 'labs_preview_started',
  /**
   * Per-step progress, first time each step is reached. Shares the sprint's STEP IDS (see
   * `preview-steps.ts`) so abandon points are comparable across the two surfaces, while the EVENT name
   * stays disjoint.
   */
  STEP_VIEWED: 'labs_preview_step_viewed',
  MODULE_COMPLETED: 'labs_preview_module_completed',
  ARCHETYPE_REVEALED: 'labs_preview_archetype_revealed',
  /** Reached the payoff. This is "completed the lab" and the denominator for the email ask. */
  COMPLETED: 'labs_preview_completed',
  /** The email field was submitted. Over COMPLETED = how many finishers will give an address. */
  EMAIL_SUBMITTED: 'labs_preview_email_submitted',
  /**
   * The reader was handed to sign-up. The last event this surface owns. Whether they then finish
   * sign-up is answered by the platform's own auth events, joined on email.
   *
   * Carries `path`, either 'externship_card' or 'email_form', because the payoff offers both and the
   * board has to be able to tell them apart. If every signup arrives via a card, the email form is
   * dead weight and should be cut.
   */
  SIGNUP_HANDOFF: 'labs_preview_signup_handoff',
  /** A matched externship card was clicked from the payoff. */
  RECOMMENDATION_CLICKED: 'labs_preview_recommendation_clicked',
} as const;

export type LabsPreviewEventName = (typeof LABS_PREVIEW_EVENTS)[keyof typeof LABS_PREVIEW_EVENTS];
